import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { AnalyticsEvent, createApiClient } from '../api';
import { Link } from '../api';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import WorldHeatmap from './WorldHeatmap';

interface Props {
    link: Link;
    token: string;
    onClose: () => void;
}

type TimeRange = '7d' | '30d' | '3m' | '1y' | 'all';

const TIME_RANGES: { key: TimeRange; label: string }[] = [
    { key: '7d', label: '7J' },
    { key: '30d', label: '30J' },
    { key: '3m', label: '3M' },
    { key: '1y', label: '1A' },
    { key: 'all', label: 'Tout' },
];

// Country code to flag emoji mapping
const getCountryFlag = (countryCode: string): string => {
    if (!countryCode || countryCode.length !== 2) return '🌍';
    const codePoints = countryCode
        .toUpperCase()
        .split('')
        .map(char => 0x1F1E6 + char.charCodeAt(0) - 65);
    return String.fromCodePoint(...codePoints);
};

// Parse referrer to get source name
const parseReferrer = (referer: string | null): string => {
    if (!referer) return 'Direct';
    try {
        const url = new URL(referer);
        const host = url.hostname.toLowerCase();
        if (host.includes('youtube')) return 'YouTube';
        if (host.includes('twitter') || host.includes('x.com')) return 'X (Twitter)';
        if (host.includes('instagram')) return 'Instagram';
        if (host.includes('facebook')) return 'Facebook';
        if (host.includes('linkedin')) return 'LinkedIn';
        if (host.includes('tiktok')) return 'TikTok';
        if (host.includes('google')) return 'Google';
        if (host.includes('bing')) return 'Bing';
        if (host.includes('reddit')) return 'Reddit';
        return host.replace('www.', '');
    } catch {
        return 'Direct';
    }
};

// Get device icon
const getDeviceIcon = (device: string): string => {
    const d = device.toLowerCase();
    if (d.includes('mobile') || d.includes('phone')) return '📱';
    if (d.includes('tablet')) return '📱';
    return '💻';
};

// Helper to parse DB timestamp (which is UTC but stored without 'Z')
const parseTimestamp = (ts: string): Date => {
    // DB format: "2026-01-05 19:06:44" -> treat as UTC
    const normalized = ts.replace(' ', 'T') + 'Z';
    return new Date(normalized);
};

const InlineAnalytics = ({ link, token, onClose }: Props) => {
    const [events, setEvents] = useState<AnalyticsEvent[]>([]);
    const [loading, setLoading] = useState(true);
    const [timeRange, setTimeRange] = useState<TimeRange>('30d');
    const [isLive, setIsLive] = useState(true);
    const [clickFlash, setClickFlash] = useState(false);
    const prevCountRef = useRef<number>(0);

    // Fetch analytics data
    const fetchAnalytics = useCallback(async () => {
        const client = createApiClient({ baseUrl: '/api', token });
        try {
            const data = await client.getAnalytics(link.slug);
            // Check if new clicks since last fetch
            if (prevCountRef.current > 0 && data.length > prevCountRef.current) {
                setClickFlash(true);
                setTimeout(() => setClickFlash(false), 1000);
            }
            prevCountRef.current = data.length;
            setEvents(data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [link.slug, token]);

    // Initial fetch
    useEffect(() => {
        fetchAnalytics();
    }, [fetchAnalytics]);

    // Polling every 5 seconds when live
    useEffect(() => {
        if (!isLive) return;
        const interval = setInterval(fetchAnalytics, 5000);
        return () => clearInterval(interval);
    }, [isLive, fetchAnalytics]);

    // Calculate date cutoff based on time range
    const getDateCutoff = (range: TimeRange): Date | null => {
        const now = new Date();
        switch (range) {
            case '7d':
                return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            case '30d':
                return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
            case '3m':
                return new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
            case '1y':
                return new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
            case 'all':
                return null;
        }
    };

    // Filter events by time range
    const filteredEvents = useMemo(() => {
        const cutoff = getDateCutoff(timeRange);
        console.log('[Analytics] Raw events:', events.length, events);
        console.log('[Analytics] Cutoff:', cutoff);
        const filtered = cutoff
            ? events.filter(e => parseTimestamp(e.timestamp) >= cutoff)
            : events;
        console.log('[Analytics] Filtered events:', filtered.length);
        return filtered;
    }, [events, timeRange]);

    // Aggregate events by day for the chart
    const chartData = useMemo(() => {
        const cutoff = getDateCutoff(timeRange);

        // Group by date (YYYY-MM-DD in UTC)
        const grouped: Record<string, number> = {};
        filteredEvents.forEach(e => {
            const parsed = parseTimestamp(e.timestamp);
            const date = parsed.toISOString().split('T')[0];
            console.log('[Analytics] Event:', e.timestamp, '->', date);
            grouped[date] = (grouped[date] || 0) + 1;
        });
        console.log('[Analytics] Grouped:', grouped);

        // Fill in missing dates with 0
        const dates: { date: string; clicks: number }[] = [];
        if (Object.keys(grouped).length > 0 || cutoff) {
            const sortedDates = Object.keys(grouped).sort();
            const startDate = cutoff || (sortedDates.length > 0 ? new Date(sortedDates[0]) : new Date());
            const endDate = new Date();

            const current = new Date(startDate);
            while (current <= endDate) {
                const dateStr = current.toISOString().split('T')[0];
                dates.push({
                    date: dateStr,
                    clicks: grouped[dateStr] || 0,
                });
                current.setDate(current.getDate() + 1);
            }
        }

        return dates;
    }, [filteredEvents, timeRange]);

    // Top 5 Countries
    const topCountries = useMemo(() => {
        const counts: Record<string, number> = {};
        filteredEvents.forEach(e => {
            const country = e.country || 'Unknown';
            counts[country] = (counts[country] || 0) + 1;
        });
        const total = filteredEvents.length || 1;
        return Object.entries(counts)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 5)
            .map(([code, count]) => ({
                code,
                flag: getCountryFlag(code),
                count,
                percent: Math.round((count / total) * 100),
            }));
    }, [filteredEvents]);

    // Traffic sources (referrers)
    const trafficSources = useMemo(() => {
        const counts: Record<string, number> = {};
        filteredEvents.forEach(e => {
            const source = parseReferrer(e.referer);
            counts[source] = (counts[source] || 0) + 1;
        });
        const total = filteredEvents.length || 1;
        return Object.entries(counts)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 5)
            .map(([name, count]) => ({
                name,
                count,
                percent: Math.round((count / total) * 100),
            }));
    }, [filteredEvents]);

    // Device breakdown
    const devices = useMemo(() => {
        const counts: Record<string, number> = {};
        filteredEvents.forEach(e => {
            const device = e.device || 'Unknown';
            counts[device] = (counts[device] || 0) + 1;
        });
        const total = filteredEvents.length || 1;
        return Object.entries(counts)
            .sort((a, b) => b[1] - a[1])
            .map(([name, count]) => ({
                name,
                icon: getDeviceIcon(name),
                count,
                percent: Math.round((count / total) * 100),
            }));
    }, [filteredEvents]);

    // Compute stats for the selected range
    const rangeClicks = chartData.reduce((sum, d) => sum + d.clicks, 0);
    const totalClicks = events.length; // Use events.length for real-time updates

    // Format date for tooltip
    const formatDate = (dateStr: string) => {
        const d = new Date(dateStr);
        return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
    };

    // Format date for X axis (shorter)
    const formatAxisDate = (dateStr: string) => {
        const d = new Date(dateStr);
        return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
    };

    // Custom tooltip
    const CustomTooltip = ({ active, payload, label }: any) => {
        if (active && payload && payload.length) {
            return (
                <div className="chart-tooltip">
                    <span className="chart-tooltip-date">{formatDate(label)}</span>
                    <span className="chart-tooltip-value">{payload[0].value} clics</span>
                </div>
            );
        }
        return null;
    };

    return (
        <div className="inline-panel inline-analytics">
            <div className="inline-panel-header">
                <span className="inline-panel-title">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"></line><line x1="12" y1="20" x2="12" y2="4"></line><line x1="6" y1="20" x2="6" y2="14"></line></svg>
                    Analytics
                    {isLive && (
                        <span className="live-indicator" onClick={() => setIsLive(false)} title="Cliquer pour désactiver">
                            <span className="live-dot"></span>
                            Live
                        </span>
                    )}
                </span>
                <button className="ghost-button" onClick={onClose} aria-label="Close">✕</button>
            </div>
            <div className="inline-panel-body">
                {loading ? (
                    <p className="muted">Chargement...</p>
                ) : (
                    <>
                        {/* Stats row */}
                        <div className={`inline-stats ${clickFlash ? 'click-flash' : ''}`}>
                            <div className="inline-stat">
                                <span className="inline-stat-value">{totalClicks}</span>
                                <span className="inline-stat-label">Total</span>
                            </div>
                            <div className="inline-stat">
                                <span className="inline-stat-value">{rangeClicks}</span>
                                <span className="inline-stat-label">Période</span>
                            </div>
                        </div>

                        {/* Time range selector */}
                        <div className="chart-time-selector">
                            {TIME_RANGES.map(({ key, label }) => (
                                <button
                                    key={key}
                                    className={`chart-time-btn ${timeRange === key ? 'active' : ''}`}
                                    onClick={() => setTimeRange(key)}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>

                        {/* Chart */}
                        <div className="chart-container">
                            {chartData.length > 0 ? (
                                <ResponsiveContainer width="100%" height={140}>
                                    <AreaChart data={chartData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                                        <defs>
                                            <linearGradient id="colorClicks" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                                                <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                                            </linearGradient>
                                        </defs>
                                        <XAxis
                                            dataKey="date"
                                            tickFormatter={formatAxisDate}
                                            tick={{ fontSize: 10 }}
                                            tickLine={false}
                                            axisLine={false}
                                            interval="preserveStartEnd"
                                        />
                                        <YAxis
                                            tick={{ fontSize: 10 }}
                                            tickLine={false}
                                            axisLine={false}
                                            allowDecimals={false}
                                        />
                                        <Tooltip content={<CustomTooltip />} />
                                        <Area
                                            type="monotone"
                                            dataKey="clicks"
                                            stroke="#3b82f6"
                                            strokeWidth={2}
                                            fillOpacity={1}
                                            fill="url(#colorClicks)"
                                        />
                                    </AreaChart>
                                </ResponsiveContainer>
                            ) : (
                                <p className="muted chart-empty">Aucune donnée pour cette période</p>
                            )}
                        </div>

                        {/* Analytics breakdown sections */}
                        {filteredEvents.length > 0 && (
                            <div className="analytics-breakdown">
                                {/* Country List (Heatmap) */}
                                <WorldHeatmap countryData={
                                    topCountries.reduce((acc, c) => {
                                        acc[c.code] = c.count;
                                        return acc;
                                    }, {} as Record<string, number>)
                                } />
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default InlineAnalytics;
